import type { CanvasElement } from '@/typings/customilyElements/canvasElements';
import type { ListElements } from '@/typings/customilyElements/layer';
import type { ElementsPageEps } from '@/typings/customilyElements/elementsPageEps';
import type { ElementsGroup } from '@/typings/customilyElements/elementsGroup';
import { ListElementType } from '@/typings/customilyElements/listElementType';
import type { LayersTreeState } from '../types';
import { CanvasEx } from '@/typings/canvas';
import { Commit } from 'vuex';
import { getUniqueEpsIds, getEpsId } from './elementUtils';
import { handleGroupStatus } from './elementUtils/handle/group';
import { isChildOf, findElementPath, collectElements, compareArrays } from './treeUtils';
import { setGroupVisibility } from '@/utils/elementsGroupUtils';
import { selectCanvasElements, areElementsEqualByUuidOrId } from '@/utils/elementsUtils';
import { handlePageEpsStatus } from '@/utils/elementsPageEpsUtils';

export const handlePageEpsElement = (
    data: {
        element: ListElements;
        canvas: CanvasEx;
    },
    selectedItems: ListElements[]
) => {
    const { element, canvas } = data;
    if (!canvas) return;

    const pageEpsElement = element;
    const pageEpsIds = getUniqueEpsIds([pageEpsElement]);

    handlePageEpsStatus(canvas, pageEpsIds);

    const groupElements = canvas.getElementsGroups() || [];

    const filteredGroups = groupElements.filter((group) => group.pageEpsId === pageEpsElement.element.id);

    setGroupVisibility(filteredGroups, canvas, true);
    canvas?.setActivePageEps(pageEpsElement.element as ElementsPageEps, true);
    canvas?.selectElementsFromPageEps(pageEpsElement.element as ElementsPageEps);
    selectedItems.push(pageEpsElement);
};

export const handleElementsSelection = (
    data: {
        elements: ListElements[];
        canvas: CanvasEx;
        fromStepOne?: boolean;
    },
    commit: Commit
) => {
    try {
        const { canvas, fromStepOne } = data;
        commit('clearSelectedItems', {
            canvas,
            preventStateMutation: fromStepOne
        });

        if (!data.elements.length || !canvas) return;

        const pageEpsElement = data.elements.find((element) => element.type === ListElementType.PageEps);
        if (pageEpsElement) {
            const selectedItems = [] as ListElements[];
            handlePageEpsElement(
                {
                    element: pageEpsElement,
                    canvas
                },
                selectedItems
            );
            commit('setSelectedItems', {
                selectedItems,
                canvas,
                preventStateMutation: fromStepOne
            });
            return;
        }

        const canvasAndGroupElements = data.elements.filter(
            (element) => element.type === ListElementType.CanvasElement || element.type === ListElementType.Group
        );

        const uniqueEpsIds = getUniqueEpsIds(canvasAndGroupElements);
        handlePageEpsStatus(canvas, uniqueEpsIds);

        handleElementsSelectionWithCtrlKey(
            {
                elements: canvasAndGroupElements,
                canvas,
                fromStepOne
            },
            commit
        );
    } catch (error) {
        console.error('An error occurred trying to handle the elements selection', error);
    }
};

export const handleElementsSelectionWithCtrlKey = (
    data: {
        elements: ListElements[];
        canvas: CanvasEx;
        fromStepOne?: boolean;
    },
    commit: Commit
) => {
    try {
        const { elements, canvas, fromStepOne } = data;
        if (!elements.length || !canvas) return;

        const filterElements = (source: ListElements[], target: ListElements[]) =>
            source.filter((item) => !target.some((targetItem) => areElementsEqualByUuidOrId(targetItem, item)));

        let filteredSelectedItems = filterElements(canvas._selectedItems || [], elements);
        let filteredDataElements = filterElements(elements, canvas._selectedItems || []);

        if (!filteredDataElements.length && !filteredSelectedItems.length) {
            commit('clearSelectedItems', {
                canvas,
                preventStateMutation: fromStepOne
            });
            canvas?.discardActiveObject();
            return;
        }

        const activePageEps = canvas?.getActivePageEps();
        const currentEpsId = activePageEps[0]?.id || null;
        const newEpsId = filteredDataElements[0] ? getEpsId(filteredDataElements[0]) : null;

        const epsPage = elements.find((element) => element.type === ListElementType.PageEps);
        if (epsPage) {
            if (!canvas?.isPreviewStep) {
                // Skip in preview mode because this selects elements from the selected EPS and deselects elements from other EPS
                handleElementsSelection(data, commit);
                return;
            }
            if (!epsPage.children) {
                return;
            }
            //In preview mode, elements from the newly selected EPS are added to the array of already selected elements
            filteredDataElements = filterElements(epsPage.children, canvas._selectedItems || []);
        }

        if (newEpsId && !canvas?.isPreviewStep) {
            filteredSelectedItems = filteredSelectedItems.filter((item) => getEpsId(item) === newEpsId);
            filteredDataElements = filteredDataElements.filter((item) => getEpsId(item) === newEpsId);
        }

        const removeChildElements = (parentArray: ListElements[], childArray: ListElements[]) => {
            for (let i = parentArray.length - 1; i >= 0; i--) {
                const parentElement = parentArray[i];
                for (let j = childArray.length - 1; j >= 0; j--) {
                    const childElement = childArray[j];
                    if (isChildOf(parentElement, childElement) || isChildOf(childElement, parentElement)) {
                        parentArray.splice(i, 1);
                        break;
                    }
                }
            }
        };

        removeChildElements(filteredSelectedItems, filteredDataElements);

        const elementsByType = [...filteredSelectedItems, ...filteredDataElements].reduce((acc, element) => {
            acc[element.type] = acc[element.type] || [];
            if (!acc[element.type].some((e) => areElementsEqualByUuidOrId(e, element))) {
                acc[element.type].push(element);
            }
            return acc;
        }, {} as Record<string, ListElements[]>);

        const selectedItems: ListElements[] = [];
        const activeCanvasElements: CanvasElement[] = [];

        const handleElements = (type: string, callback: (elements: ListElements[]) => void) => {
            const elements = elementsByType[type] || [];
            if (elements.length) {
                callback(elements);
                selectedItems.push(...elements);
            }
        };

        handleElements('Group', (elements) => {
            elements.forEach((item) => {
                const groupElements = canvas?.getGroupElements(item.element as string | ElementsGroup) || [];
                activeCanvasElements.push(...groupElements);
            });
        });

        handleElements('CanvasElement', (elements) => {
            activeCanvasElements.push(...elements.map((item) => item.element as CanvasElement));
        });

        if (newEpsId && newEpsId !== currentEpsId) {
            handlePageEpsStatus(canvas, [newEpsId]);
            const pageEpsElements = canvas.getElementsPageEps() || [];
            const epsElement = pageEpsElements.find((item) => item.id === newEpsId);
            if (epsElement) {
                canvas?.setActivePageEps(epsElement, true);
            }
        }

        handleGroupStatus(
            {
                canvas: canvas
            },
            selectedItems
        );
        if (activeCanvasElements && activeCanvasElements.length) {
            selectCanvasElements(activeCanvasElements, canvas, true);
        }

        commit('setSelectedItems', {
            selectedItems,
            canvas,
            preventStateMutation: fromStepOne
        });
    } catch (error) {
        console.error('An error occurred trying to handle the elements selection', error);
    }
};

export const handleElementsSelectionWithShiftKey = (
    data: { elements: ListElements[]; canvas: CanvasEx; fromStepOne?: boolean },
    state: LayersTreeState,
    commit: Commit
): void => {
    try {
        const { elements, canvas, fromStepOne } = data;
        if (!elements.length || !canvas) return;

        if (!canvas._selectedItems || canvas._selectedItems.length === 0) {
            handleElementsSelection(data, commit);
            return;
        }

        const endElement = elements[elements.length - 1];
        const firstSelected = canvas._selectedItems[0];
        const lastSelected = canvas._selectedItems[canvas._selectedItems.length - 1];

        const endPath = findElementPath(state.treeItems, endElement.element.uuid!);
        const firstPath = findElementPath(state.treeItems, firstSelected.element.uuid!);

        if (!endPath || !firstPath) throw new Error('Failed to find element paths');

        const startElement = compareArrays(endPath, firstPath) < 0 ? lastSelected : firstSelected;
        const startPath = findElementPath(state.treeItems, startElement.element.uuid!);

        if (!startPath) throw new Error('Failed to find start element path');

        const filteredDataElements = collectElements(state.treeItems, startPath, endPath).filter((item) => {
            return !canvas._selectedItems?.some((selected) => areElementsEqualByUuidOrId(selected, item));
        });

        for (const element of filteredDataElements) {
            handleElementsSelectionWithCtrlKey(
                {
                    elements: [element],
                    canvas,
                    fromStepOne
                },
                commit
            );
        }
    } catch (error) {
        console.error('An error occurred trying to handle the elements selection', error);
    }
};
